// FT3D party vote — the table poll panel.
//
// The GM asks the question out loud and hits the vote button in the GM controls; every player
// gets Yes / No / Abstain, and each answered character's portrait slides into its column live.
// The GM can close early, and the poll closes itself at the deadline.
//
// The browser owns the poll. The socket retains its frames for reconnects; PHP saves only the closing verdict.

import { decideVote, mergeVote, voteActor, voteComplete, type VoteMessage, type VotePoll, type VoteChoice, type VoteResult } from '../janus-engine/presentation/party-vote';
import { pushEphemeralTruth } from '../shared/ephemeral-truth';
import { pickAudioTake, seededRandom, peekAudioCatalog } from '../shared/audio-catalog';
import type { PresentationContext } from '../janus-engine/presentation/ft3d';
type VoteCue = 'called' | 'choice' | 'ballot' | 'expired' | 'decided';

const RESULT_TEXT: Record<VoteResult, string> = {
    yes: 'The party votes Yes.',
    no: 'The party votes No.',
    divided: 'The party is divided.',
    abstain: 'The party abstains.',
};

// A closed poll stays up briefly so everyone reads the verdict, then gets out of the scene's way.
const RESULT_LINGER_MS = 9000;
// Non-GM clients wait past the deadline before firing the auto-close, so the GM's own client
// normally writes it and a late straggler still closes a poll the GM has walked away from.
const PLAYER_AUTOCLOSE_GRACE_MS = 2500;
// Floor between deadline-close attempts from one client, so a refused close never becomes a loop.
const CLOSE_RETRY_INTERVAL_MS = 4000;
// Key the remembered corner in the shared FT3D window-position store.
const PANEL_POSITION_NAME = 'ft3d_party_vote';

let poll: VotePoll | null = null;
/** Local ms at which the current poll expires, re-based off the SERVER clock on every frame. */
let deadlineAtMs = 0;
let tickTimer: number | null = null;
let lingerTimer: number | null = null;
// In-flight state is keyed by POLL ID, never a bare boolean: a response for a poll the table has
// already moved past must not unlock (or lock) the ballot of the poll now on screen.
let closeInFlightPollId: string | null = null;
let castInFlightPollId: string | null = null;
let lastCloseAttemptMs = 0;
// The newest poll this client has ever seen, and every poll it has finished with. Frames arrive
// out of order (a WS broadcast can overtake an HTTP response), so ordering is decided by the
// poll's own openedAt rather than by arrival — and a retired poll never comes back.
let newestOpenedAt = 0;
let newestPollId = '';
const retiredPollIds = new Set<string>();
// Cue urls for the poll on screen, resolved server-side when it opened.
let cueUrls: Partial<Record<VoteCue, string>> = {};
// The ballots already seated, so a frame can tell a NEW answer from a redelivered one and tick once.
let seatedBallotIds = new Set<string>();
let rosterObserver: MutationObserver | null = null;
let mountedPanel: HTMLElement | null = null;
const seats = new Map<string, HTMLElement>();

/**
 * Play one of the poll's client-side cues.
 *
 * Registered as a site-audio transient so the campaign's master mute governs it like every other
 * game sound; a poll is a world event, not interface chrome. A cue whose pool SoundForge has not
 * ingested yet is simply absent from the map and silently skipped — never substituted.
 */
function playVoteCue(cue: VoteCue): void {
    const url = cueUrls[cue];
    if (!url) return;
    try {
        const audio = new Audio(url);
        window.ftRegisterSiteAudioTransient?.(audio);
        void audio.play().catch(() => { /* autoplay policy, or the viewer muted the tab */ });
    } catch { /* a cue that will not construct is never worth breaking the poll over */ }
}

/** Adopt the cue urls a poll was opened with. Later frames omit them; keep what we have. */
function adoptCues(cues: unknown): void {
    if (!cues || typeof cues !== 'object') return;
    (['called', 'choice', 'ballot', 'expired', 'decided'] as const).forEach((key) => {
        const url = (cues as Record<string, unknown>)[key];
        if (typeof url === 'string' && url.startsWith('http')) cueUrls[key] = url;
    });
}

function normalizedId(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
}

function surface(): HTMLElement | null {
    return document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
}

function campaignId(): string {
    return normalizedId(surface()?.dataset.campaignId);
}

function viewerIsGm(): boolean {
    return surface()?.dataset.ft3dCanManage === '1';
}

/**
 * The character this viewer votes with. The active caster is preferred (it is who they are
 * playing right now), but only when it is actually on the ballot — a player driving a summon
 * must still vote as their own party member, not lose the ballot to a creature that has none.
 */
function voteContext(): PresentationContext {
    const ft = window.FateTide as unknown as Record<string, any>;
    const encounterId = String(surface()?.dataset.encounterId ?? '');
    return { campaignId: campaignId(), encounterId, userId: String(ft?.user?.id ?? ''),
        actorId: String(ft?.activeViewerCasterId ?? surface()?.dataset.ft3dInitialCasterId ?? ''),
        canManage: viewerIsGm(), campaign: ft?.campaignCache?.get(campaignId()) ?? ft?.campaignArr ?? null,
        encounter: ft?.encounterCache?.get(encounterId) ?? ft?.encounterArr ?? null };
}
function ballotActorId(): string { return poll ? voteActor(voteContext(), poll) : ''; }

function ensurePanel(): HTMLElement | null {
    const root = surface();
    if (!root) return null;
    if (mountedPanel && root.contains(mountedPanel)) return mountedPanel;

    const panel = document.createElement('aside');
    panel.className = 'ft3d-party-vote';
    panel.id = 'ft3d_party_vote_window';
    panel.dataset.ft3dPartyVote = '';
    // Joins the shared active-window contract (ft3d-hud/active-window.ts) purely by carrying this
    // attribute: a pointer press inside it makes it the active window and lights its title bar,
    // exactly like Settings / Workspace / the dice window.
    //
    // ⛔ Its X is deliberately NOT `data-ft3d-window-close`. Escape closes the active window
    // through that selector, and here "close" ends the poll for the WHOLE TABLE and writes the
    // verdict row — far too destructive to hang off a stray Escape. With no matching control,
    // closeActiveFt3dWindow() declines and Escape falls through untouched.
    panel.dataset.ft3dWindow = 'party-vote';
    panel.setAttribute('aria-label', 'Party vote');
    panel.setAttribute('role', 'region');
    panel.hidden = true;
    panel.innerHTML = `
        <header class="ft3d-party-vote__bar ft3d-party-vote__bar--grabbable" data-ft3d-party-vote-drag>
            <strong class="ft3d-party-vote__title">Party vote</strong>
            <span class="ft3d-party-vote__timer" data-ft3d-party-vote-timer role="timer"></span>
            <button type="button" class="ft3d-party-vote__close ft-window-close" data-ft3d-party-vote-close
                title="End the vote now" aria-label="End the vote now" hidden>&times;</button>
        </header>
        <div class="ft3d-party-vote__columns">
            <section class="ft3d-party-vote__column ft3d-party-vote__column--yes">
                <h4>Yes <span data-ft3d-party-vote-count="yes">0</span></h4>
                <div class="ft3d-party-vote__seats" data-ft3d-party-vote-column="yes"></div>
            </section>
            <section class="ft3d-party-vote__column ft3d-party-vote__column--no">
                <h4>No <span data-ft3d-party-vote-count="no">0</span></h4>
                <div class="ft3d-party-vote__seats" data-ft3d-party-vote-column="no"></div>
            </section>
        </div>
        <div class="ft3d-party-vote__aside">
            <div class="ft3d-party-vote__tray" data-ft3d-party-vote-tray="pending">
                <span class="ft3d-party-vote__tray-label">Deciding</span>
                <div class="ft3d-party-vote__seats" data-ft3d-party-vote-column="pending"></div>
            </div>
            <div class="ft3d-party-vote__tray" data-ft3d-party-vote-tray="abstain">
                <span class="ft3d-party-vote__tray-label">Abstained</span>
                <div class="ft3d-party-vote__seats" data-ft3d-party-vote-column="abstain"></div>
            </div>
        </div>
        <div class="ft3d-party-vote__ballot" data-ft3d-party-vote-ballot hidden>
            <button type="button" data-ft3d-party-vote-cast="yes">Yes</button>
            <button type="button" data-ft3d-party-vote-cast="no">No</button>
            <button type="button" data-ft3d-party-vote-cast="abstain">Abstain</button>
        </div>
        <p class="ft3d-party-vote__note" data-ft3d-party-vote-note role="status" aria-live="polite"></p>`;

    panel.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const castButton = target.closest<HTMLElement>('[data-ft3d-party-vote-cast]');
        if (castButton) {
            const choice = castButton.dataset.ft3dPartyVoteCast;
            if (choice === 'yes' || choice === 'no' || choice === 'abstain') void castVote(choice);
            return;
        }
        if (target.closest('[data-ft3d-party-vote-close]')) void requestClose('gm');
    });

    root.appendChild(panel);
    mountedPanel = panel;

    // Share the FT3D window kit rather than growing a second dragger: same freeze-then-pin
    // measurement, same clamping to the play surface, same per-viewer remembered corner as the
    // Settings / Workspace / dice windows. The kit refuses to start a drag from a control, so the
    // X in the bar still closes the vote.
    const drag = window.ftMakeFt3dWindowDraggable;
    const restore = window.ftRestoreFt3dWindowPosition;
    const bar = panel.querySelector<HTMLElement>('[data-ft3d-party-vote-drag]');
    if (typeof drag === 'function' && bar) drag(PANEL_POSITION_NAME, panel, bar, root);
    if (typeof restore === 'function') restore(PANEL_POSITION_NAME, panel, root);

    return panel;
}

/**
 * The Party frame has already resolved every member's portrait, so a seat reuses that image
 * rather than re-deriving one. Initials stand in for a member the roster has not rendered yet
 * (an inactive PC, or a roster still loading) — the observer repaints when it arrives.
 */
function seatFor(playerCharacterId: string, displayName: string): HTMLElement {
    const seat = document.createElement('span');
    seat.className = 'ft3d-party-vote__seat';
    seat.dataset.ft3dPartyVoteSeat = playerCharacterId;
    seat.title = displayName;
    const member = document.querySelector<HTMLElement>(
        `[data-ft3d-party-player-id="${CSS.escape(playerCharacterId)}"]`,
    );
    const portrait = member?.querySelector<HTMLImageElement>('.ft3d-party-member__portrait img');
    if (portrait?.src) {
        const image = document.createElement('img');
        image.src = portrait.src;
        image.alt = displayName;
        image.draggable = false;
        seat.appendChild(image);
    } else {
        const initials = document.createElement('span');
        initials.className = 'ft3d-party-vote__initials';
        initials.textContent = displayName ? displayName.slice(0, 2) : '??';
        seat.appendChild(initials);
    }
    return seat;
}

function remainingMs(): number {
    if (!poll || poll.status !== 'open') return 0;
    return Math.max(0, deadlineAtMs - Date.now());
}

function render(): void {
    const panel = ensurePanel();
    if (!panel) return;
    if (!poll) {
        panel.hidden = true;
        return;
    }
    panel.hidden = false;
    panel.dataset.ft3dPartyVoteStatus = poll.status;
    // Stamped so a smoke (or anyone reading a live table's DOM) can tell THIS poll from the one
    // before it — the panel is reused across polls rather than rebuilt.
    panel.dataset.ft3dPartyVotePollId = poll.pollId;
    // The window kit FREEZES the measured height when it pins a window — right for a window with
    // its own inner scroll, wrong for this one, which grows a tray and shrinks it again as the
    // table answers. Hand the height back to the content after every state change (but never
    // mid-drag, where the kit is actively re-measuring).
    if (panel.style.height && !panel.classList.contains('is-dragging')) {
        panel.style.removeProperty('height');
    }

    const byChoice = new Map(poll.ballots.map(ballot => [normalizedId(ballot.playerCharacterId), ballot.choice]));
    const buckets: Record<'yes' | 'no' | 'abstain' | 'pending', HTMLElement[]> = {
        yes: [], no: [], abstain: [], pending: [],
    };
    poll.eligible.forEach(entry => {
        const id = normalizedId(entry.playerCharacterId);
        const bucket = byChoice.get(id) ?? 'pending';
        let seat = seats.get(id);
        if (!seat) { seat = seatFor(id, entry.displayName); seats.set(id, seat); }
        buckets[bucket].push(seat);
    });
    const eligible = new Set(poll.eligible.map(entry => normalizedId(entry.playerCharacterId)));
    for (const [id, seat] of seats) if (!eligible.has(id)) { seat.remove(); seats.delete(id); }

    (['yes', 'no', 'abstain', 'pending'] as const).forEach(bucket => {
        const column = panel.querySelector<HTMLElement>(`[data-ft3d-party-vote-column="${bucket}"]`);
        if (column) for (const seat of buckets[bucket]) if (seat.parentElement !== column) column.appendChild(seat);
        if (bucket === 'yes' || bucket === 'no') {
            const count = panel.querySelector<HTMLElement>(`[data-ft3d-party-vote-count="${bucket}"]`);
            if (count) count.textContent = String(buckets[bucket].length);
        } else {
            const tray = panel.querySelector<HTMLElement>(`[data-ft3d-party-vote-tray="${bucket}"]`);
            // A tray with nobody in it is noise. The Yes/No columns always stay, so the shape of
            // the poll never jumps around underneath a voter mid-click.
            if (tray) tray.hidden = buckets[bucket].length === 0;
        }
    });

    renderTimer();

    const closeButton = panel.querySelector<HTMLElement>('[data-ft3d-party-vote-close]');
    if (closeButton) closeButton.hidden = !viewerIsGm() || poll.status !== 'open';

    const actorId = ballotActorId();
    const ballot = panel.querySelector<HTMLElement>('[data-ft3d-party-vote-ballot]');
    if (ballot) {
        ballot.hidden = poll.status !== 'open' || actorId === '';
        const mine = actorId ? byChoice.get(actorId) : undefined;
        ballot.querySelectorAll<HTMLButtonElement>('[data-ft3d-party-vote-cast]').forEach(button => {
            const choice = button.dataset.ft3dPartyVoteCast;
            button.classList.toggle('is-chosen', choice === mine);
            button.setAttribute('aria-pressed', choice === mine ? 'true' : 'false');
            button.disabled = castInFlightPollId === poll?.pollId;
        });
    }

    const note = panel.querySelector<HTMLElement>('[data-ft3d-party-vote-note]');
    if (note) {
        if (poll.status === 'closed') {
            note.textContent = poll.result ? RESULT_TEXT[poll.result] : 'The vote is closed.';
        } else if (actorId === '') {
            note.textContent = viewerIsGm() ? 'The table is voting.' : 'You have no character on this ballot.';
        } else {
            note.textContent = '';
        }
    }
}

/**
 * The countdown alone. Kept OUT of render(): the clock ticks four times a second, and rebuilding
 * every seat on each tick would swap out live <img> elements under the viewer's eye — a live clock
 * inside a full re-render is exactly what makes a panel flicker and eat clicks.
 */
function renderTimer(): void {
    const timer = mountedPanel?.querySelector<HTMLElement>('[data-ft3d-party-vote-timer]');
    if (!timer || !poll) return;
    const text = poll.status === 'open' ? `${Math.ceil(remainingMs() / 1000)}s` : 'Closed';
    if (timer.textContent !== text) timer.textContent = text;
}

function stopTicking(): void {
    if (tickTimer !== null) {
        window.clearInterval(tickTimer);
        tickTimer = null;
    }
}

function tick(): void {
    if (!poll || poll.status !== 'open') {
        stopTicking();
        return;
    }
    renderTimer();
    // Every client races the same deadline; the endpoint's close-lock plus its poll-id-derived
    // roll_id are what keep the log row singular, so a redundant fire is harmless — but a REJECTED
    // close must not be retried four times a second, hence the backoff in requestClose().
    const overdueBy = Date.now() - deadlineAtMs;
    if (overdueBy >= (viewerIsGm() ? 0 : PLAYER_AUTOCLOSE_GRACE_MS)) void requestClose('deadline');
}

function startTicking(): void {
    stopTicking();
    if (!poll || poll.status !== 'open') return;
    tickTimer = window.setInterval(tick, 250);
}

function applyPoll(
    next: VotePoll | null,
    serverNow: number,
    cues?: Partial<Record<VoteCue, string>>,
): void {
    // The sidecar outlives the poll by design (a player who reloads right after a close still
    // reads the verdict), so a page load hours later must NOT resurrect a decision the table has
    // long since acted on. Anything older than the on-screen linger is history, not a result.
    if (next && next.status === 'closed' && next.closedAt > 0 && Number.isFinite(serverNow)
        && (serverNow - next.closedAt) * 1000 > RESULT_LINGER_MS) {
        next = null;
    }
    if (next) {
        // A poll this client has already finished with never comes back — not as a late "cast"
        // broadcast, not as a closed frame arriving after the panel retired.
        if (retiredPollIds.has(next.pollId)) return;
        // Strictly older than the newest poll seen => a frame that lost the race in flight.
        if (next.pollId !== newestPollId && next.openedAt < newestOpenedAt) return;
        if (poll && poll.pollId === next.pollId && poll.status === 'closed' && next.status === 'open') return;
        if (next.openedAt >= newestOpenedAt) {
            newestOpenedAt = next.openedAt;
            newestPollId = next.pollId;
        }
    } else if (poll) {
        // A late "there is no poll" answer (the mount-time state fetch resolving after an open
        // broadcast landed) must not wipe the poll the table is currently answering.
        return;
    }

    const isNewPoll = !poll || !next || poll.pollId !== next.pollId;
    if (isNewPoll) {
        // Scope both in-flight guards to the poll now on screen. Leaving castInFlight set would
        // leave the new ballot disabled until an unrelated old request settled.
        castInFlightPollId = null;
        closeInFlightPollId = null;
        lastCloseAttemptMs = 0;
        cueUrls = {};
    }
    adoptCues(cues);

    const wasOpen = poll?.status === 'open';
    const previousBallots = seatedBallotIds;
    seatedBallotIds = new Set((next?.ballots ?? []).map(b => normalizedId(b.playerCharacterId)));
    poll = next;

    // CUES. Fired from HERE rather than from each caller so a poll reaching this client by
    // broadcast, by its own HTTP response, or by the mount-time state fetch all sound the same —
    // and so a frame applyPoll rejected above can never sound at all. The four verdict stingers
    // are not among these: they ride the closing log row like any other combat-log audio.
    if (next && isNewPoll && next.status === 'open') {
        playVoteCue('called');
    } else if (next && !isNewPoll && wasOpen && next.status === 'closed') {
        // Classify the close by the server's timestamps, not receipt time: a final ballot's
        // early result can arrive after the deadline without becoming an expiry.
        playVoteCue(next.closedAt >= next.closesAt ? 'expired' : 'decided');
    } else if (next && next.status === 'open') {
        const mine = ballotActorId();
        const arrived = [...seatedBallotIds].filter(id => !previousBallots.has(id));
        // One tick per frame however many ballots it carried: a burst of identical ticks reads as
        // noise rather than as the table answering.
        if (arrived.length > 0) playVoteCue(arrived.includes(mine) ? 'choice' : 'ballot');
    }

    if (lingerTimer !== null) {
        window.clearTimeout(lingerTimer);
        lingerTimer = null;
    }
    if (!next) {
        stopTicking();
        render();
        return;
    }

    // Re-base the countdown on the SERVER's clock every time a frame lands: the browser's absolute
    // clock never enters the arithmetic, so a skewed machine still expires with the table. For one
    // poll the deadline may only ever move EARLIER — otherwise a delayed frame carrying an older
    // `now` would silently hand that client extra seconds to vote.
    const anchor = Number.isFinite(serverNow) && serverNow > 0 ? serverNow : next.closesAt;
    const rebased = Date.now() + Math.max(0, (next.closesAt - anchor) * 1000);
    deadlineAtMs = isNewPoll ? rebased : Math.min(deadlineAtMs, rebased);

    if (next.status === 'open') {
        startTicking();
    } else {
        stopTicking();
        lingerTimer = window.setTimeout(() => {
            lingerTimer = null;
            retiredPollIds.add(next.pollId);
            // Only retire the panel if this same closed poll is still the one on screen.
            if (poll && poll.pollId === next.pollId && poll.status === 'closed') {
                poll = null;
                seatedBallotIds = new Set();
                render();
            }
        }, RESULT_LINGER_MS);
    }
    render();
}

async function postVote(body: Record<string, unknown>): Promise<VoteMessage> {
    const ctx = voteContext();
    if (!ctx.encounterId || !ctx.campaignId) throw new Error('party-vote:context-required');
    const message = decideVote(ctx, poll, body, Date.now() / 1000);
    if (message.reason === 'open') {
        const catalog = peekAudioCatalog();
        message.cues = {};
        if (catalog) for (const cue of ['called', 'choice', 'ballot', 'expired', 'decided']) {
            const take = pickAudioTake(catalog, [[`party_vote/${cue}`]], seededRandom(`${message.poll.pollId}:${cue}`));
            if (take) message.cues[cue] = take.url;
        }
    }
    if (message.reason === 'close') {
        // This lane has one durable duty: save the exact browser-decided verdict, once.
        const response = await fetch(`${window.BASE_URL || '/'}options/game/encounter/ft3d_party_vote_verdict_save_inc.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ encounter_id: message.poll.encounterId || ctx.encounterId,
                actor_id: '', operation_id: message.poll.pollId, row_kind: 'party_vote_verdict', relay: false,
                roll_type_1: ({ yes: 'the_party_votes_yes', no: 'the_party_votes_no', divided: 'the_party_is_divided', abstain: 'the_party_abstains' })[message.poll.result!],
                rolls_full: RESULT_TEXT[message.poll.result!] }), signal: AbortSignal.timeout(20000),
        });
        const saved = await response.json();
        if (!response.ok || saved.ok !== true) throw new Error(saved.error || 'party-vote:verdict-save-refused');
        const catalog = peekAudioCatalog();
        message.log = { roll_i: saved.roll_i, roll_id: message.poll.pollId, roll_type_1: ({ yes: 'the_party_votes_yes', no: 'the_party_votes_no', divided: 'the_party_is_divided', abstain: 'the_party_abstains' })[message.poll.result!],
            encounter_id: message.poll.encounterId || ctx.encounterId, campaign_id: ctx.campaignId,
            roll_by: saved.roll_by, roll_by_item_type: 'user', roll_by_item_name: 'Game Master', roll_private: 0,
            rolls_full: RESULT_TEXT[message.poll.result!], roll_result: 0,
            roll_sound: { v: 2, ...(catalog ? { rev: catalog.rev } : {}), voice: 0 } };
    }
    if (!pushEphemeralTruth(message)) throw new Error('party-vote:transport-unavailable');
    receiveVote(message);
    return message;
}
function receiveVote(message: VoteMessage): void {
    if (!message) return;
    if (message.schema !== 'ft.ft3d.party-vote.v1' || normalizedId(message.campaign_id) !== campaignId()) return;
    // Preserve the pre-merge ballots for cue detection; mergeVote owns only the changed seat.
    const next = mergeVote(poll, message);
    if (!next) return;
    applyPoll(next, Number(message.now) + Number(message.transport_replay_age_ms ?? 0) / 1000, message.cues);
    if (poll?.status === 'open' && voteComplete(poll) && viewerIsGm()) void requestClose('deadline');
    else if (poll?.status === 'open' && voteComplete(poll)) {
        const pollId = poll.pollId;
        window.setTimeout(() => {
            if (poll?.pollId === pollId && poll.status === 'open' && voteComplete(poll)) void requestClose('deadline');
        }, PLAYER_AUTOCLOSE_GRACE_MS);
    }
}

function showNote(text: string): void {
    const note = ensurePanel()?.querySelector<HTMLElement>('[data-ft3d-party-vote-note]');
    if (note) note.textContent = text;
}

async function castVote(choice: VoteChoice): Promise<void> {
    const actorId = ballotActorId();
    if (!poll || poll.status !== 'open' || !actorId) return;
    const pollId = poll.pollId;
    if (castInFlightPollId === pollId) return;
    castInFlightPollId = pollId;
    render();
    try {
        const message = await postVote({
            party_vote_action: 'cast',
            party_vote_poll_id: pollId,
            party_vote_actor_id: actorId,
            party_vote_choice: choice,
        });
        // Only clear the guard belonging to the poll THIS request was for; a newer poll's guard is
        // not ours to release.
        if (castInFlightPollId === pollId) castInFlightPollId = null;

    } catch (error) {
        if (castInFlightPollId === pollId) castInFlightPollId = null;
        render();
        // A vote the server rejected as belonging to an earlier poll is not the voter's problem —
        // their panel has already moved on, so say nothing rather than blame them for a race.
        const text = error instanceof Error ? error.message : 'Your vote could not be recorded.';
        if (poll?.pollId === pollId) showNote(text);
    }
}

async function requestClose(source: 'gm' | 'deadline'): Promise<void> {
    if (!poll || poll.status !== 'open') return;
    const pollId = poll.pollId;
    if (closeInFlightPollId === pollId) return;
    // The deadline check runs at 4Hz. A close the server refuses (Redis wobble, or another client
    // holding the lock) must not become a hot retry loop against the endpoint.
    const nowMs = Date.now();
    if (source === 'deadline' && nowMs - lastCloseAttemptMs < CLOSE_RETRY_INTERVAL_MS) return;
    lastCloseAttemptMs = nowMs;
    closeInFlightPollId = pollId;
    try {
        const message = await postVote({ party_vote_action: 'close', party_vote_poll_id: pollId });

    } catch (error) {
        // A deadline close losing the race (another client got there first, or the GM's own close
        // landed a beat earlier) is the expected path, not a failure worth showing anyone.
        if (source === 'gm' && poll?.pollId === pollId) {
            showNote(error instanceof Error ? error.message : 'The vote could not be closed.');
        }
    } finally {
        if (closeInFlightPollId === pollId) closeInFlightPollId = null;
    }
}

/** The GM's "call a party vote" button in the turn-widget GM controls. */
async function openPartyVote(seconds?: number): Promise<void> {
    if (!viewerIsGm()) return;
    try {
        const message = await postVote({
            party_vote_action: 'open',
            party_vote_seconds: Number.isFinite(seconds) ? seconds : 60,
        });
        // The GM's own client sounds the call from this response rather than waiting on the
        // broadcast round-trip; applyPoll owns the firing either way.

    } catch (error) {
        const warn = (window as unknown as { displayWarning?: (text: string) => void }).displayWarning;
        const text = error instanceof Error ? error.message : 'The party vote could not be opened.';
        if (typeof warn === 'function') warn(text);
        else showNote(text);
    }
}


export function initializeFt3dPartyVote(): void {
    const root = surface();
    if (!root || root.dataset.ft3dPartyVoteMounted === '1') return;
    root.dataset.ft3dPartyVoteMounted = '1';

    window.addEventListener('ft3d-party-vote-message', event => {
        receiveVote((event as CustomEvent<VoteMessage>).detail);
    });
    (window as Window & { ftDrainPartyVoteMessages?: () => void }).ftDrainPartyVoteMessages?.();

    // Seats borrow the Party frame's portraits, so repaint once the roster (re)renders.
    const roster = root.querySelector('[data-ft3d-party-roster]');
    if (roster && typeof MutationObserver === 'function') {
        rosterObserver = new MutationObserver(() => { if (poll) render(); });
        rosterObserver.observe(roster, { childList: true, subtree: true });
    }

    // The socket subscription replays the retained browser frames; no PHP state fetch.

}

const mountPartyVote = (): void => initializeFt3dPartyVote();
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountPartyVote, { once: true });
else mountPartyVote();

declare global {
    interface Window {
        BASE_URL?: string;
        initializeFt3dPartyVote?: () => void;
        ftFt3dOpenPartyVote?: (seconds?: number) => void;
        // Published by the ft3d-hud bundle (pre-template), which evaluates well before this one.
        ftMakeFt3dWindowDraggable?: (
            name: string, win: HTMLElement, bar: HTMLElement, frame: HTMLElement | null,
        ) => void;
        ftRestoreFt3dWindowPosition?: (
            name: string, win: HTMLElement, frame: HTMLElement | null,
        ) => void;
        /** Binds a transient sound to the campaign's site-audio master mute. */
        ftRegisterSiteAudioTransient?: (audio: HTMLAudioElement) => HTMLAudioElement;
    }
}

window.initializeFt3dPartyVote = initializeFt3dPartyVote;
window.ftFt3dOpenPartyVote = (seconds?: number): void => { void openPartyVote(seconds); };
